import axios, { type AxiosInstance, AxiosError } from 'axios'
import { i18n } from '@/i18n'
import type { Complaint, ComplaintCategory, ComplaintSummary, Ticket, TicketDetail, TicketFile, TicketPriority } from '@/utils/tickets'
import type { PasskeyOptions } from '@/utils/passkeys'
import type { CaptchaAnswer } from '@/utils/captcha'
import type { ExpLevel } from '@/utils/lvBadge'

// One definition of the storage key: the request interceptor reads it,
// the 401 handler clears it, and the auth store persists it.
export const TOKEN_KEY = 'cdnx_token'

// HTTP client targeting the control plane. The base URL is /api so it
// proxies through Vite in dev and is served by the same nginx in prod.
const client: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 30_000,
  withCredentials: false,
})

// Inject the JWT from local storage on every request.
client.interceptors.request.use((config) => {
  const tok = localStorage.getItem(TOKEN_KEY)
  if (tok) {
    config.headers = config.headers ?? {}
    config.headers.Authorization = `Bearer ${tok}`
  }
  return config
})

// Server-side messages arrive as a stable code plus the values the sentence
// interpolates, with the English text alongside as "message". Looking the
// code up in the console's own catalogue is what makes a Chinese customer
// see Chinese guidance: translating on the server would mean carrying the
// request's locale through every service and putting UI text in the domain
// layer.
//
// Falling back to the server's English is deliberate rather than showing a
// raw key: an error nobody has translated yet is still worth reading, and a
// bare "domain.originNotPublic" on screen would be worse than the sentence
// it replaced.
export function localizeError(code: string | undefined, message: string | undefined, params?: Record<string, unknown>) {
  if (code) {
    const key = `serverErrors.${code}`
    const t = i18n.global.t as (k: string, p?: Record<string, unknown>) => string
    const translated = t(key, params ?? {})
    if (translated !== key) return translated
  }
  return message ?? ''
}

// Centralised error shaping. Handlers always reject with `ApiError`.
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly requestId?: string
  // The server's own English text, kept next to the localized `message` so
  // a bug report can quote what the control plane actually said.
  readonly rawMessage: string
  constructor(status: number, code: string, message: string, requestId?: string, rawMessage?: string) {
    super(message)
    this.status = status
    this.code = code
    this.requestId = requestId
    this.rawMessage = rawMessage ?? message
  }
}

client.interceptors.response.use(
  (r) => r,
  (err: AxiosError<{ code?: string; message?: string; request_id?: string; params?: Record<string, unknown> }>) => {
    if (err.response) {
      const { status, data } = err.response
      // An expired session used to surface as a failed request on every
      // page and nothing else: the console stayed on a screen it could not
      // load until the user happened to reload, which is when the router
      // guard finally noticed. Clearing the token and going to the login
      // page is what the 401 actually means.
      //
      // `window.location` rather than the router on purpose — the router
      // imports the auth store, which imports this module, and reaching
      // back for it here would close that cycle.
      //
      // Only when the request carried the token still held: one sent just
      // before 修改密码 or 退出其他设备 handed this session a new token comes
      // back 401 too, and must not sign the session out.
      const sent = (err.config?.headers as Record<string, unknown> | undefined)?.Authorization
      const held = localStorage.getItem(TOKEN_KEY)
      const stale = !!sent && sent !== `Bearer ${held}`
      if (status === 401 && !stale && !window.location.pathname.startsWith('/login')) {
        localStorage.removeItem(TOKEN_KEY)
        const next = window.location.pathname + window.location.search
        window.location.assign('/login?next=' + encodeURIComponent(next))
      }
      throw new ApiError(
        status,
        data?.code ?? 'HTTP_' + status,
        localizeError(data?.code, data?.message ?? err.message, data?.params),
        data?.request_id,
        data?.message ?? err.message,
      )
    }
    throw new ApiError(0, 'NETWORK', err.message)
  },
)

// Public surface.
// 套餐功能开关 and 数量配额 — mirrors control/internal/service.SiteEntitlements
// and PackageAllowances. A count's max 0 is no ceiling.
export type Feature = 'waf' | 'custom_cc' | 'websocket' | 'http3' | 'custom_cert' | 'openapi' | 'l4'
export const FEATURES: Feature[] = ['waf', 'custom_cc', 'websocket', 'http3', 'custom_cert', 'openapi', 'l4']
export interface Allowance {
  used: number
  max: number
}
export interface SiteEntitlements {
  package: { id: number; name: string } | null
  /** The caller is held to it — a customer; an operator is not. */
  enforced: boolean
  features: Record<Feature, boolean>
  rules: Allowance
  sites: Allowance
  hostnames: Allowance
  apexes: Allowance
}
export interface PackageAllowances {
  features: Record<Feature, boolean>
  sites: Allowance
  hostnames: Allowance
  apexes: Allowance
  rules_per_site: number
  /** 应用加速 rules under the package. */
  l4?: Allowance
}

// Mirrors control/internal/service.Subscription (cancelled ones are not
// sent to customers).
export interface MySubscription {
  id: number
  package_id: number
  package_name: string
  started_at: string
  expires_at: string
  state: 'active' | 'expiring' | 'expired'
  sites: number
  /** 自动续费: renewed from the balance in its last 3 days, for
   *  auto_renew_period ('' is a month). */
  auto_renew: boolean
  auto_renew_period: Period | ''
  /** Why the last automatic renewal failed (a server error code), '' when
   *  it went through or none was tried. */
  auto_renew_error: string
  usage?: import('@/components/QuotaUsage.vue').QuotaUsageData
  allowances?: PackageAllowances
}

// 充值 — mirrors control/internal/service.BillingOverview, OrderView and
// TopUpStart, and model.LedgerEntry. Every amount is 分.
export type PayMethod = 'page' | 'wap' | 'qr'
export type TopUpState = 'pending' | 'paid' | 'closed' | 'expired'
export interface BillingOverview {
  balance_cents: number
  paid_total_cents: number
  paid_orders: number
  pending_orders: number
  /** What the account can pay with now, in order: page, wap, qr. */
  methods: PayMethod[]
  min_cents: number
  max_cents: number
  presets: number[]
  /** What packages have cost since the time asked about (?since=). */
  spent_since_cents?: number
}
export interface TopUpOrder {
  id: number
  out_trade_no: string
  amount_cents: number
  channel: string
  method: PayMethod
  state: TopUpState
  trade_no: string
  buyer: string
  paid_at?: string
  expires_at: string
  created_at: string
}
export interface TopUpStart {
  order: TopUpOrder
  /** Alipay's cashier, for page and wap. */
  pay_url?: string
  /** The code to scan, for qr. */
  qr_code?: string
}
export interface LedgerEntry {
  id: number
  kind: 'topup' | 'adjust' | 'remittance' | 'withdraw' | 'withdraw_back' | 'refund' | 'refund_back' | 'purchase' | 'order_refund'
    | 'realname'
    | 'realname_back'
  /** Signed: a credit is positive. */
  amount_cents: number
  /** The balance after it. */
  balance_cents: number
  order_id?: number
  /** A top-up's order number. */
  order_no?: string
  /** The finance document behind other changes: PO…, RM…, WD…, RF…. */
  ref_no?: string
  note: string
  created_at: string
}

// 财务管理 — mirrors control/internal/service FinanceOverview, StorePackage,
// PackageOrderView and the finance documents (model). Every amount is 分.
export type Period = 'month' | 'quarter' | 'year'
export type DocStatus = 'pending' | 'processing' | 'success' | 'failed' | 'approved' | 'rejected' | 'cancelled' | 'issued'
export interface BankAccount {
  id: string
  name: string
  bank: string
  account: string
  note: string
}
export interface FinanceOverview {
  balance_cents: number
  paid_in_cents: number
  refunded_cents: number
  withdrawn_cents: number
  invoiced_cents: number
  /** Paid in, not paid out again, not invoiced: what can still be invoiced. */
  uninvoiced_cents: number
  /** What can be withdrawn or refunded now. */
  cashable_cents: number
  spent_cents: number
  pending_remittances: number
  pending_withdrawals: number
  pending_invoices: number
  pending_refunds: number
  store: { enabled: boolean }
  remittance: { enabled: boolean; accounts: BankAccount[]; min_cents: number; note: string }
  withdraw: { enabled: boolean; ready: boolean; min_cents: number; max_cents: number; fee_permille: number; fee_min_cents: number }
  invoice: { enabled: boolean; normal: boolean; special: boolean; electronic: boolean; paper: boolean; min_cents: number; note: string }
  refund: { enabled: boolean; topup_days: number; package_days: number }
}
/** A package on sale (service.StorePackage). A count of 0 is no ceiling; a
 *  protection figure or log retention of 0 is not stated. */
export interface StorePackage {
  id: number
  name: string
  description: string
  /** Its 套餐分组; none for an ungrouped package. */
  group_id?: number
  group_name?: string
  prices: Partial<Record<Period, number>>
  traffic_quota_gb: number
  request_quota_million: number
  bandwidth_mbps: number
  max_connections: number
  site_quota: number
  domain_quota: number
  apex_domain_quota: number
  rule_quota: number
  /** 应用加速 rules; 0 is no ceiling, when features.l4. */
  l4_port_quota: number
  cc_protection_qps: number
  ddos_protection_gbps: number
  log_retention_days: number
  features: Record<Feature, boolean>
}
export interface PackageOrder {
  id: number
  order_no: string
  package_id: number
  package_name: string
  kind: 'buy' | 'renew'
  period: Period
  months: number
  amount_cents: number
  refunded_cents: number
  user_package_id: number
  period_start: string
  period_end: string
  created_at: string
  state: 'paid' | 'partly_refunded' | 'refunded'
}
export interface FinanceFile {
  id: number
  name: string
  size: number
  content_type: string
}
export interface Remittance {
  id: number
  remit_no: string
  amount_cents: number
  account_name: string
  account_bank: string
  account_no: string
  payer_name: string
  payer_account: string
  transferred_at: string
  reference: string
  receipt_id?: number
  note: string
  status: DocStatus
  review_note: string
  reviewed_at?: string
  created_at: string
}
export interface WithdrawAccount {
  id: number
  kind: string
  real_name: string
  account: string
  label: string
  created_at: string
}
export interface Withdrawal {
  id: number
  withdraw_no: string
  amount_cents: number
  fee_cents: number
  net_cents: number
  real_name: string
  account: string
  status: DocStatus
  fail_reason: string
  review_note: string
  finished_at?: string
  created_at: string
}
export interface InvoiceTitle {
  kind: 'personal' | 'company'
  title: string
  tax_no: string
  address: string
  phone: string
  bank: string
  bank_account: string
  email: string
  recipient: string
  recipient_phone: string
  mail_address: string
}
export interface InvoiceProfile extends InvoiceTitle {
  id: number
  is_default: boolean
  updated_at: string
}
export interface Invoice extends InvoiceTitle {
  id: number
  invoice_no: string
  type: 'normal' | 'special'
  medium: 'electronic' | 'paper'
  amount_cents: number
  status: DocStatus
  number: string
  file_id?: number
  express_company: string
  express_no: string
  review_note: string
  reviewed_at?: string
  created_at: string
}
export interface Refundable {
  target: 'topup' | 'package'
  id: number
  no: string
  title: string
  amount_cents: number
  max_cents: number
  at: string
  deadline: string
}
export interface Refund {
  id: number
  refund_no: string
  target: 'topup' | 'package'
  target_no: string
  amount_cents: number
  approved_cents: number
  reason: string
  revoke_time: boolean
  status: DocStatus
  fail_reason: string
  review_note: string
  finished_at?: string
  created_at: string
}
type Page<T> = Promise<{ items: T[]; total: number }>

// 工单 — the ticket types are utils/tickets.ts's, shared with support's
// console; these are the customer's own: the filing form's choices
// (service.TicketOptions) and the counts (service.TicketSummary).
export interface TicketCategory {
  id: string
  name: string
  enabled: boolean
}
/** 账户资料's form: every field may be empty. */
export interface ProfileInput {
  nickname: string
  qq: string
  wechat: string
  backup_name: string
  backup_phone: string
  company: string
  /** A key of PROFILE_INDUSTRIES. */
  industry: string
  /** A key of PROFILE_TEAM_SIZES. */
  team_size: string
  site_usage: string
  province: string
  city: string
  address: string
  postcode: string
  /** ISO 3166-1 alpha-2; '' not said (taken as mainland China). Decides 实名认证's methods; fixed once verified. */
  country: string
}

/** 账户资料's page: the form's fields and, read only, the account. */
export interface Profile extends ProfileInput {
  id: number
  username: string
  email: string
  /** Masked: 138****0000. */
  phone: string
  phone_verified: boolean
  status: string
  realname_status: string
  realname_type: string
  avatar_url: string
  created_at: string
  updated_at: string
}

/**
 * One change to 经验 (经验记录): 每日登录 (ref the day), a 新手任务 (ref the
 * task), or the platform's (adjust: up or down, with why).
 */
export interface ExpGrant {
  id: number
  source: 'daily' | 'task' | 'adjust'
  ref: string
  amount: number
  note?: string
  created_at: string
}

/** One change to the VIP 成长值 (成长值记录): the platform's, why, and the growth and VIP 等级 after it. */
export interface GrowthRecord {
  id: number
  amount: number
  growth_after: number
  level_after: number
  note: string
  created_at: string
}

/**
 * 会员等级, the customer's: the VIP 等级, the 成长值 that makes it and where
 * the level starts and the next does (vip_next 0: the top), whether 工单优先
 * is what it brings, the level, 每日登录 (daily 0: not given) and today's,
 * the 新手任务 offered, and the latest 经验记录 and 成长值记录.
 */
export interface MemberOverview {
  vip_level: number
  vip_growth: number
  vip_floor: number
  vip_next: number
  ticket_first: boolean
  growth_records: GrowthRecord[]
  level: ExpLevel
  /** Where each colour of 等级 starts, and the top: LV → the 经验 it takes. */
  ladder: { lv: number; exp: number }[]
  daily: number
  daily_done: boolean
  tasks: { key: 'realname' | 'phone' | 'guard' | 'site'; amount: number; done: boolean }[]
  records: ExpGrant[]
}

/** 实名认证's page: the account, and what online verification offers. */
export interface RealnameView {
  status: 'unverified' | 'pending' | 'verified' | 'rejected'
  type: 'individual' | 'enterprise'
  /** Masked: 张*. */
  name?: string
  cert_no?: string
  /** How and when it passed online; empty when verified by hand. */
  method?: string
  verified_at?: string
  /** The document it passed with: '' a 居民身份证, else PASSPORT, ID_CARD, …; and who issued it. */
  cert_type?: string
  cert_country?: string
  /** The account's country (账户资料) and what follows: cn, mainland China's methods; intl, the international ones. */
  country: string
  region: 'cn' | 'intl'
  online: boolean
  /** The admin's 实名认证列表 for the region, in its order (TC_ Tencent's, ALI_ Aliyun's, SUMSUB_/VERIFF_ international, the rest Alipay's). */
  methods: string[]
  default_method: string
  daily_attempts: number
  attempts_left: number
  /** The free attempts an account has for good, and what is left of them. */
  free_attempts: number
  free_left: number
  /** What one attempt past the free ones costs, in 分 (0: free). */
  fee_cents: number
  /** What the next attempt costs, and the balance it would be paid from. */
  next_fee_cents: number
  balance_cents: number
  /** The verification under way, or the last one that did not pass. */
  current?: RealnameAttempt
}

export type RealnameProvider = 'alipay' | 'tencent' | 'aliyun' | 'sumsub' | 'veriff'

export interface RealnameAttempt {
  id: number
  provider: RealnameProvider
  method: string
  name: string
  cert_no: string
  status: 'init' | 'pending' | 'passed' | 'failed' | 'expired'
  fail_reason?: string
  /** The provider did not take it on (refused, or its service failed): no free attempt used, any fee given back. */
  not_taken?: boolean
  /** What it cost, in 分: 0 when free or given back. */
  fee_cents: number
  created_at: string
  expires_at: string
  /** While pending: the page to scan, and the address that opens it on a phone (the Alipay app, or the page). */
  certify_url?: string
  app_url?: string
}

export interface TicketOptions {
  /** Whether tickets can be filed online; existing ones stay either way. */
  enabled: boolean
  /** The platform's notice above the form. */
  notice: string
  categories: TicketCategory[]
  priorities: TicketPriority[]
}
export interface TicketSummary {
  /** With a reply from support not seen yet. */
  unread: number
  /** Waiting on support: 待处理 and 处理中. */
  active: number
  /** Waiting on the customer. */
  replied: number
  closed: number
}
export type TicketFilter = '' | 'active' | 'replied' | 'closed'
/** 我的投诉's filter: 处理中 is whatever still waits on support. */
export type ComplaintFilter = '' | 'open' | 'resolved' | 'withdrawn'

// 应用加速 — mirrors control/internal/service.L4RuleView and friends.
export type L4Protocol = 'tcp' | 'udp' | 'tcp_udp'
export type L4Balance = 'round_robin' | 'ip_hash'
export type L4Proxy = 'off' | 'v1' | 'v2'
export type L4State = 'active' | 'disabled' | 'suspended'
export interface L4Rule {
  id: number
  user_id: number
  package_id: number
  name: string
  protocol: L4Protocol
  listen_port: number
  origins: string[]
  balance: L4Balance
  proxy_protocol: L4Proxy
  status: 'active' | 'disabled'
  remark: string
  created_at: string
  updated_at: string
  package_name: string
  /** Where visitors connect, on listen_port; '' when the package has no line. */
  access_host: string
  state: L4State
  /** Why a suspended rule is: plan_expired, no_feature, or the quota's. */
  reason?: string
}
export interface L4Summary {
  total: number
  active: number
  disabled: number
  suspended: number
}
export interface L4PackageOption {
  id: number
  name: string
  allowed: boolean
  used: number
  /** 0 is no ceiling. */
  max: number
  access_host: string
}
export interface L4Options {
  port_min: number
  port_max: number
  packages: L4PackageOption[]
}
export interface L4Input {
  package_id: number
  name: string
  protocol: L4Protocol
  /** 0 picks a free port. */
  listen_port: number
  origins: string[]
  balance: L4Balance
  proxy_protocol: L4Proxy
  remark: string
}

// What /auth/login and /auth/login/mfa answer: a token, or — the password
// of an account with an MFA device — a ticket for the second step.
export interface LoginResult {
  access_token?: string
  expires_in?: number
  user?: any
  mfa_required?: boolean
  mfa_ticket?: string
  /** The ways the second step may be finished: "mfa", "sms". */
  methods?: SecondStep[]
  /** Where a texted code goes, masked: 138****0000. */
  phone?: string
}
export type SecondStep = 'mfa' | 'sms'
// A code texted: where to (masked), how long it lasts, how long until
// another may be asked for — all in seconds.
export interface SentCode {
  phone: string
  expires_in: number
  interval: number
}
/** A code mailed for registering: where to (masked), how long it holds, the wait before another. */
export interface SentEmailCode {
  email: string
  expires_in: number
  interval: number
}
/** What registering sends; the codes, the phone and agree only as the platform asks. */
export interface RegisterInput {
  username: string
  email: string
  password: string
  email_code?: string
  phone?: string
  sms_code?: string
  agree?: boolean
  captcha?: CaptchaAnswer
}
export type AgreementKind = 'terms' | 'privacy'
/** An agreement in the reader's language: its text (cleaned rich text) or its link. */
export interface Agreement {
  kind: AgreementKind
  mode: 'content' | 'link'
  content?: string
  url?: string
}
// A new token for this session (修改密码, 退出其他设备).
export interface SessionToken {
  access_token: string
  expires_in: number
}
// One sign-in, or one wrong password or code given (登录记录).
export interface LoginRecord {
  id: number
  ip: string
  user_agent: string
  result: 'success' | 'bad_password' | 'bad_mfa' | 'bad_sms' | 'inactive' | 'password_reset'
  /** How: password, code, passkey or sub_user; '' on records from before it was kept. */
  method?: string
  /** The 子用户 who signed in, when one did. */
  sub_user?: string
  created_at: string
}
// A 子用户: another sign-in to this account, with its permissions.
export interface SubUser {
  id: number
  username: string
  remark: string
  status: 'active' | 'disabled'
  last_login_at?: string
  created_at: string
  updated_at: string
}
// A new MFA device's secret and the otpauth address its QR code carries.
export interface MFASetup {
  secret: string
  uri: string
}

// 站内信: what the platform told this account (消息中心), and how it wants
// each event (消息接收配置). Both consoles.
export interface InboxItem {
  id: number
  category: string
  event: string
  title: string
  body: string
  /** A page of this console, '' for none. */
  link: string
  read_at: string | null
  created_at: string
}
export interface InboxPage {
  items: InboxItem[]
  total: number
  unread: number
}
export interface NotifyPrefRow {
  event: string
  category: string
  inbox: boolean
  mail: boolean
  /** False for support's events: their mail goes where 工单设置 sends it. */
  mail_available: boolean
}

/** An extra hostname of a site (model.DomainAlias). The nodes serve it
 *  only once active: its CNAME verified to pass through the site's own
 *  record ('cname'), forced by an admin ('admin'), or carried over from
 *  before aliases were verified ('legacy'). */
export interface DomainAlias {
  id: number
  domain_id: number
  hostname: string
  status: 'pending' | 'active'
  verified_via?: 'cname' | 'admin' | 'legacy' | ''
  verified_at?: string
  created_at: string
}

/** One look at a pending alias's CNAME (service.AliasVerifyResult). */
export interface AliasVerifyResult {
  alias: DomainAlias
  expected: string
  resolved?: string
  matched: boolean
  reason?: { code?: string; message?: string; params?: Record<string, unknown> }
}

/** What keeps a site from being served properly (service.SiteReadiness):
 *  no origin, so every request is a 502; hostnames with no certificate,
 *  whose HTTPS handshake fails; hostnames served one that does not cover
 *  them; aliases still pending CNAME verification. Empty fields are left
 *  out. */
export interface SiteReadiness {
  no_origin?: boolean
  no_cert?: string[]
  cert_mismatch?: string[]
  alias_pending?: string[]
}

// Mirrors control/internal/service.CCAutoStatus: a site's 自适应 CC
// across the nodes.
export interface CCAutoStatus {
  active: boolean
  nodes: number
  since?: string
  reason?: 'failing' | 'slow'
  rps: number
  baseline_rps: number
  failed_ratio: number
  ttfb_ms: number
}

export const api = {
  // Worded in the console's language: 站内信 are kept as their parts and
  // rendered by 消息模板's 站内信 templates for the reader.
  notifications(params: { unread?: boolean; category?: string; page?: number; page_size?: number }): Promise<InboxPage> {
    const { unread, ...rest } = params
    return client
      .get('/notifications', { params: { ...rest, unread: unread ? 1 : undefined, lang: i18n.global.locale.value } })
      .then((r) => r.data)
  },
  /** No ids: every unread one. */
  readNotifications(ids: number[]): Promise<{ unread: number }> {
    return client.post('/notifications/read', { ids }).then((r) => r.data)
  },
  deleteNotifications(ids: number[]): Promise<{ unread: number }> {
    return client.post('/notifications/delete', { ids }).then((r) => r.data)
  },
  notifyPrefs(): Promise<{ items: NotifyPrefRow[] }> {
    return client.get('/notifications/prefs').then((r) => r.data)
  },
  setNotifyPref(event: string, pref: { inbox: boolean; mail: boolean }): Promise<{ items: NotifyPrefRow[] }> {
    return client.put(`/notifications/prefs/${event}`, pref).then((r) => r.data)
  },
  // Auth. /auth/* lives at the root (not under /api/v1) because it's
  // pre-version infrastructure shared by all API versions, so we bypass
  // the axios instance's baseURL by passing an absolute path explicitly.
  /** username is the account's username, email or proved phone; remember is 「7 天内自动登录」. */
  login(username: string, password: string, remember = false, captcha?: CaptchaAnswer) {
    return client.post('/auth/login', { username, password, remember, captcha }, { baseURL: '' })
      .then((r) => r.data as LoginResult)
  },
  me() {
    return client.get('/me').then((r) => r.data)
  },
  // The second step for an account with an MFA device: the ticket the
  // password got, and the device's code.
  loginMFA(ticket: string, code: string, method: SecondStep = 'mfa', remember = false) {
    return client.post('/auth/login/mfa', { ticket, code, method, remember }, { baseURL: '' })
      .then((r) => r.data as LoginResult)
  },
  // 验证码登录: a code to a proved phone or the account's address, then the code.
  sendLoginCodeTo(target: string, captcha?: CaptchaAnswer) {
    return client.post('/auth/login/code/send', { target, captcha }, { baseURL: '' }).then((r) => r.data as SentCode)
  },
  loginByCode(target: string, code: string, remember = false) {
    return client.post('/auth/login/code', { target, code, remember }, { baseURL: '' }).then((r) => r.data as LoginResult)
  },
  // 其他登录方式. 子用户: the main account's ID or username, the sub-user's
  // name and password.
  loginSub(owner: string, username: string, password: string, remember = false, captcha?: CaptchaAnswer) {
    return client.post('/auth/login/sub', { owner, username, password, remember, captcha }, { baseURL: '' })
      .then((r) => r.data as LoginResult)
  },
  // 通行密钥: the options for the browser, then its answer.
  passkeyLoginOptions() {
    return client.post('/auth/passkey/options', null, { baseURL: '' }).then((r) => r.data as PasskeyOptions)
  },
  loginPasskey(ceremony: string, credential: Record<string, any>, remember = false) {
    return client.post('/auth/login/passkey', { ceremony, credential, remember }, { baseURL: '' })
      .then((r) => r.data as LoginResult)
  },
  // 注册: the account, and the codes proving its address and phone.
  register(input: RegisterInput) {
    return client.post('/public/register', input).then((r) => r.data as { id: number; username: string })
  },
  sendRegisterEmailCode(email: string, captcha?: CaptchaAnswer) {
    return client.post('/public/register/email-code', { email, captcha }).then((r) => r.data as SentEmailCode)
  },
  sendRegisterSmsCode(phone: string, captcha?: CaptchaAnswer) {
    return client.post('/public/register/sms-code', { phone, captcha }).then((r) => r.data as SentCode)
  },
  agreement(kind: AgreementKind, lang: string) {
    return client.get(`/public/agreements/${kind}`, { params: { lang } }).then((r) => r.data as Agreement)
  },
  // 登录保护 by text: the code for the account the ticket was handed to.
  sendLoginCode(ticket: string) {
    return client.post('/auth/login/sms', { ticket }, { baseURL: '' }).then((r) => r.data as SentCode)
  },
  // 找回密码: a code to the account's proved phone, then the new password.
  sendResetCode(username: string, captcha?: CaptchaAnswer) {
    return client.post('/auth/password/code', { username, captcha }, { baseURL: '' }).then((r) => r.data as SentCode)
  },
  resetPassword(username: string, code: string, newPassword: string) {
    return client.post('/auth/password/reset', { username, code, new_password: newPassword }, { baseURL: '' })
  },

  // 安全中心: the signed-in account's own. Changing the password and ending
  // the other sessions hand this session a new token (useAuthStore().useToken).
  accountLogins(limit = 50) {
    return client.get('/account/logins', { params: { limit } }).then((r) => (r.data.items ?? []) as LoginRecord[])
  },
  changePassword(current: string, next: string) {
    return client.put('/account/password', { current_password: current, new_password: next })
      .then((r) => r.data as SessionToken)
  },
  updateContact(body: { password: string; email: string; phone: string }) {
    return client.put('/account/contact', body).then((r) => r.data)
  },
  newMFASecret() {
    return client.post('/account/mfa/secret').then((r) => r.data as MFASetup)
  },
  enableMFA(body: { password: string; secret: string; code: string }) {
    return client.post('/account/mfa', body).then((r) => r.data)
  },
  disableMFA(code: string) {
    return client.post('/account/mfa/disable', { code }).then((r) => r.data)
  },
  endOtherSessions() {
    return client.post('/account/sessions/end-others').then((r) => r.data as SessionToken)
  },
  // Proving a phone with a texted code; an empty phone removes it.
  sendPhoneCode(phone: string, captcha?: CaptchaAnswer) {
    return client.post('/account/phone/code', { phone, captcha }).then((r) => r.data as SentCode)
  },
  changePhone(body: { password: string; phone: string; code: string }) {
    return client.put('/account/phone', body).then((r) => r.data)
  },
  setLoginSMS(password: string, enabled: boolean) {
    return client.put('/account/login-sms', { password, enabled }).then((r) => r.data)
  },
  // A 子用户's own password; hands back a new token.
  changeSubPassword(current: string, next: string) {
    return client.put('/account/sub/password', { current_password: current, new_password: next })
      .then((r) => r.data as SessionToken)
  },

  // 子用户: the account's other sign-ins (the main account only).
  subUsers(): Promise<{ items: SubUser[]; max: number }> {
    return client.get('/sub-users').then((r) => ({ items: r.data.items ?? [], max: r.data.max }))
  },
  createSubUser(body: { username: string; password: string; remark: string }): Promise<SubUser> {
    return client.post('/sub-users', body).then((r) => r.data)
  },
  updateSubUser(id: number, body: { remark?: string; status?: SubUser['status'] }): Promise<SubUser> {
    return client.put(`/sub-users/${id}`, body).then((r) => r.data)
  },
  resetSubUserPassword(id: number, password: string) {
    return client.put(`/sub-users/${id}/password`, { password })
  },
  deleteSubUser(id: number) {
    return client.delete(`/sub-users/${id}`)
  },

  // Domains.
  // Which sites are suspended for an expired package: id -> reason.
  domainSuspensions(): Promise<{ items: Record<string, string> }> {
    return client.get('/domain-suspensions').then((r) => r.data)
  },
  // What each site is missing to be served properly (sites missing
  // nothing are left out), and one site's.
  domainReadinessAll(): Promise<{ items: Record<string, SiteReadiness> }> {
    return client.get('/domain-readiness').then((r) => r.data)
  },
  domainReadiness(id: number): Promise<SiteReadiness> {
    return client.get(`/domains/${id}/readiness`).then((r) => r.data)
  },
  listDomains() {
    return client.get('/domains').then((r) => r.data)
  },
  createDomain(body: {
    name: string
    package_id: number
    ssl_mode?: string
    proxy_mode?: string
    http2?: boolean
    http3?: boolean
    websocket?: boolean
    ipv6?: boolean
    real_ip_header?: string
  }) {
    return client.post('/domains', body).then((r) => r.data)
  },
  deleteDomain(id: number) {
    return client.delete(`/domains/${id}`).then((r) => r.data)
  },
  // 我的套餐: the caller's subscriptions with expiry and state.
  listMySubscriptions(): Promise<{ items: MySubscription[] }> {
    return client.get('/packages/subscriptions').then((r) => r.data)
  },
  /** With since, an ISO time, also what packages have cost since then. */
  billingOverview(params?: { since?: string }): Promise<BillingOverview> {
    return client.get('/billing', { params }).then((r) => r.data)
  },
  startTopUp(amountCents: number, method: PayMethod): Promise<TopUpStart> {
    return client.post('/billing/topups', { amount_cents: amountCents, method }).then((r) => r.data)
  },
  listTopUps(page: number, pageSize: number): Promise<{ items: TopUpOrder[]; total: number }> {
    return client.get('/billing/topups', { params: { page, page_size: pageSize } }).then((r) => r.data)
  },
  /** One order; one still unpaid is asked after at Alipay first. */
  getTopUp(id: number): Promise<TopUpOrder> {
    return client.get(`/billing/topups/${id}`).then((r) => r.data)
  },
  /** The ledger, newest first: of some kinds, made since and before two times. */
  listLedger(
    page: number,
    pageSize: number,
    filter: { kinds?: string[]; since?: string; until?: string } = {},
  ): Promise<{ items: LedgerEntry[]; total: number }> {
    const params = { page, page_size: pageSize, kind: filter.kinds?.join(',') || undefined, since: filter.since, until: filter.until }
    return client.get('/billing/ledger', { params }).then((r) => r.data)
  },

  // 财务管理.
  financeOverview(): Promise<FinanceOverview> {
    return client.get('/finance').then((r) => r.data)
  },
  financeStore(): Promise<{ packages: StorePackage[]; renewals: Record<string, Partial<Record<Period, number>>> }> {
    return client.get('/finance/store').then((r) => r.data)
  },
  buyPackage(packageId: number, period: Period): Promise<PackageOrder> {
    return client.post('/finance/orders', { kind: 'buy', package_id: packageId, period }).then((r) => r.data)
  },
  renewPackage(subscriptionId: number, period: Period): Promise<PackageOrder> {
    return client.post('/finance/orders', { kind: 'renew', subscription_id: subscriptionId, period }).then((r) => r.data)
  },
  /** 自动续费 on, for a period, or off. */
  setAutoRenew(subscriptionId: number, enabled: boolean, period?: Period): Promise<MySubscription> {
    return client.put(`/finance/subscriptions/${subscriptionId}/auto-renew`, { enabled, period }).then((r) => r.data)
  },
  listPackageOrders(page: number, pageSize: number): Page<PackageOrder> {
    return client.get('/finance/orders', { params: { page, page_size: pageSize } }).then((r) => r.data)
  },
  uploadFinanceFile(file: File, progress?: (pct: number) => void): Promise<FinanceFile> {
    const form = new FormData()
    form.append('file', file)
    return client
      .post('/finance/uploads', form, {
        timeout: 120_000,
        onUploadProgress: (e) => progress?.(Math.min(99, Math.round((e.loaded * 100) / (e.total || file.size || 1)))),
      })
      .then((r) => r.data)
  },
  downloadFinanceFile(id: number): Promise<Blob> {
    return client.get(`/finance/uploads/${id}`, { responseType: 'blob' }).then((r) => r.data)
  },
  listRemittances(page: number, pageSize: number): Page<Remittance> {
    return client.get('/finance/remittances', { params: { page, page_size: pageSize } }).then((r) => r.data)
  },
  submitRemittance(body: {
    account_id: string
    amount_cents: number
    payer_name: string
    payer_account: string
    transferred_at: string
    reference: string
    receipt_id: number
    note: string
  }): Promise<Remittance> {
    return client.post('/finance/remittances', body).then((r) => r.data)
  },
  cancelRemittance(id: number) {
    return client.post(`/finance/remittances/${id}/cancel`).then((r) => r.data)
  },
  listWithdrawAccounts(): Page<WithdrawAccount> {
    return client.get('/finance/withdraw-accounts').then((r) => r.data)
  },
  addWithdrawAccount(body: { real_name: string; account: string; label: string }): Promise<WithdrawAccount> {
    return client.post('/finance/withdraw-accounts', body).then((r) => r.data)
  },
  deleteWithdrawAccount(id: number) {
    return client.delete(`/finance/withdraw-accounts/${id}`).then((r) => r.data)
  },
  listWithdrawals(page: number, pageSize: number): Page<Withdrawal> {
    return client.get('/finance/withdrawals', { params: { page, page_size: pageSize } }).then((r) => r.data)
  },
  applyWithdrawal(accountId: number, amountCents: number): Promise<Withdrawal> {
    return client.post('/finance/withdrawals', { account_id: accountId, amount_cents: amountCents }).then((r) => r.data)
  },
  cancelWithdrawal(id: number) {
    return client.post(`/finance/withdrawals/${id}/cancel`).then((r) => r.data)
  },
  listInvoiceProfiles(): Page<InvoiceProfile> {
    return client.get('/finance/invoice-profiles').then((r) => r.data)
  },
  saveInvoiceProfile(id: number, body: InvoiceTitle & { is_default: boolean }): Promise<InvoiceProfile> {
    return (id ? client.put(`/finance/invoice-profiles/${id}`, body) : client.post('/finance/invoice-profiles', body)).then((r) => r.data)
  },
  deleteInvoiceProfile(id: number) {
    return client.delete(`/finance/invoice-profiles/${id}`).then((r) => r.data)
  },
  listInvoices(page: number, pageSize: number): Page<Invoice> {
    return client.get('/finance/invoices', { params: { page, page_size: pageSize } }).then((r) => r.data)
  },
  applyInvoice(body: { profile_id: number; type: string; medium: string; amount_cents: number }): Promise<Invoice> {
    return client.post('/finance/invoices', body).then((r) => r.data)
  },
  cancelInvoice(id: number) {
    return client.post(`/finance/invoices/${id}/cancel`).then((r) => r.data)
  },
  listRefundables(): Page<Refundable> {
    return client.get('/finance/refundables').then((r) => r.data)
  },
  listRefunds(page: number, pageSize: number): Page<Refund> {
    return client.get('/finance/refunds', { params: { page, page_size: pageSize } }).then((r) => r.data)
  },
  applyRefund(body: { target: string; target_id: number; amount_cents: number; reason: string }): Promise<Refund> {
    return client.post('/finance/refunds', body).then((r) => r.data)
  },
  cancelRefund(id: number) {
    return client.post(`/finance/refunds/${id}/cancel`).then((r) => r.data)
  },
  // 账户资料.
  profile(): Promise<Profile> {
    return client.get('/account/profile').then((r) => r.data)
  },
  saveProfile(body: ProfileInput): Promise<Profile> {
    return client.put('/account/profile', body).then((r) => r.data)
  },
  /** A cropped picture as the avatar; answers its address. */
  uploadAvatar(picture: Blob): Promise<{ avatar_url: string }> {
    const form = new FormData()
    form.append('file', picture, 'avatar.jpg')
    return client.post('/account/avatar', form, { timeout: 60_000 }).then((r) => r.data)
  },
  clearAvatar() {
    return client.delete('/account/avatar').then((r) => r.data)
  },
  // 会员等级: what the account has earned is given first.
  memberOverview(): Promise<MemberOverview> {
    return client.get('/account/member').then((r) => r.data)
  },
  // 实名认证.
  realname(): Promise<RealnameView> {
    return client.get('/realname').then((r) => r.data)
  },
  startRealname(body: { method: string; cert_name?: string; cert_no?: string; phone?: string; lang?: string }): Promise<RealnameAttempt> {
    return client.post('/realname/verify', body).then((r) => r.data)
  },
  /** How a verification stands; final is 「我已完成认证」. */
  checkRealname(id: number, final = false): Promise<RealnameAttempt> {
    return client.get(`/realname/verify/${id}`, { params: final ? { final: 1 } : {} }).then((r) => r.data)
  },
  // 工单.
  ticketOptions(): Promise<TicketOptions> {
    return client.get('/tickets/options').then((r) => r.data)
  },
  ticketSummary(): Promise<TicketSummary> {
    return client.get('/tickets/summary').then((r) => r.data)
  },
  /** The caller's tickets, the latest activity first; search is a title or a ticket number. */
  listTickets(params: { status?: TicketFilter; search?: string; page: number; page_size: number }): Page<Ticket> {
    return client.get('/tickets', { params: { ...params, status: params.status || undefined, search: params.search || undefined } }).then((r) => r.data)
  },
  /** The title is made from the description's opening. */
  createTicket(body: {
    category: string
    priority: TicketPriority
    body: string
    domain_id?: number
    attachment_ids: number[]
  }): Promise<Ticket> {
    return client.post('/tickets', body).then((r) => r.data)
  },
  /** One ticket and its conversation; opening it marks support's replies read. */
  getTicket(id: number): Promise<TicketDetail> {
    return client.get(`/tickets/${id}`).then((r) => r.data)
  },
  /** format 'html' is the rich reply box's; confidential is 保密发送. */
  replyTicket(id: number, body: { body: string; format?: '' | 'html'; confidential?: boolean; attachment_ids: number[] }): Promise<Ticket> {
    return client.post(`/tickets/${id}/replies`, body).then((r) => r.data)
  },
  closeTicket(id: number): Promise<Ticket> {
    return client.post(`/tickets/${id}/close`).then((r) => r.data)
  },
  rateTicket(id: number, rating: number, comment: string): Promise<Ticket> {
    return client.post(`/tickets/${id}/rate`, { rating, comment }).then((r) => r.data)
  },
  /** A file for a message not sent yet; the message then names its id. */
  uploadTicketFile(file: File, progress?: (pct: number) => void): Promise<TicketFile> {
    const form = new FormData()
    form.append('file', file)
    return client
      .post('/tickets/uploads', form, {
        timeout: 120_000,
        onUploadProgress: (e) => progress?.(Math.min(99, Math.round((e.loaded * 100) / (e.total || file.size || 1)))),
      })
      .then((r) => r.data)
  },
  downloadTicketFile(id: number): Promise<Blob> {
    return client.get(`/tickets/attachments/${id}`, { responseType: 'blob', timeout: 120_000 }).then((r) => r.data)
  },

  // 我的投诉: complaints about the service, about a ticket or not.
  /** The caller's complaints, the newest first; ticket_id narrows them to one ticket's. */
  listComplaints(params: { status?: ComplaintFilter; ticket_id?: number; search?: string; page: number; page_size: number }): Page<Complaint> {
    return client
      .get('/complaints', { params: { ...params, status: params.status || undefined, search: params.search || undefined } })
      .then((r) => r.data)
  },
  getComplaint(id: number): Promise<Complaint> {
    return client.get(`/complaints/${id}`).then((r) => r.data)
  },
  complaintSummary(): Promise<ComplaintSummary> {
    return client.get('/complaints/summary').then((r) => r.data)
  },
  fileComplaint(body: { category: ComplaintCategory; content: string; ticket_id?: number }): Promise<Complaint> {
    return client.post('/complaints', body).then((r) => r.data)
  },
  withdrawComplaint(id: number): Promise<Complaint> {
    return client.post(`/complaints/${id}/withdraw`).then((r) => r.data)
  },
  listL4Rules(params: { state?: L4State | ''; search?: string; page: number; page_size: number }): Page<L4Rule> {
    return client
      .get('/l4-rules', { params: { ...params, state: params.state || undefined, search: params.search || undefined } })
      .then((r) => r.data)
  },
  l4Summary(): Promise<L4Summary> {
    return client.get('/l4-rules/summary').then((r) => r.data)
  },
  l4Options(): Promise<L4Options> {
    return client.get('/l4-rules/options').then((r) => r.data)
  },
  createL4Rule(body: L4Input): Promise<L4Rule> {
    return client.post('/l4-rules', body).then((r) => r.data)
  },
  updateL4Rule(id: number, body: L4Input): Promise<L4Rule> {
    return client.put(`/l4-rules/${id}`, body).then((r) => r.data)
  },
  setL4RuleEnabled(id: number, on: boolean): Promise<L4Rule> {
    return client.post(`/l4-rules/${id}/${on ? 'enable' : 'disable'}`).then((r) => r.data)
  },
  deleteL4Rule(id: number): Promise<void> {
    return client.delete(`/l4-rules/${id}`).then(() => undefined)
  },
  // One rule's statistics over a window, in the browser's time zone.
  l4RuleStats(id: number, range: import('@/utils/l4stats').L4Range): Promise<import('@/utils/l4stats').L4Report> {
    return client
      .get(`/l4-rules/${id}/stats`, { params: { range, tz_offset: -new Date().getTimezoneOffset() } })
      .then((r) => r.data)
  },
  listMyPackages() {
    return client.get('/packages/mine').then((r) => r.data)
  },
  publishDomain(id: number) {
    return client.post(`/domains/${id}/publish`).then((r) => r.data)
  },
  verifyDomainCname(id: number) {
    return client.post(`/domains/${id}/verify`).then((r) => r.data)
  },
  disableDomain(id: number) {
    return client.post(`/domains/${id}/disable`).then((r) => r.data)
  },
  enableDomain(id: number) {
    return client.post(`/domains/${id}/enable`).then((r) => r.data)
  },
  // 自助切换套餐: moves a site to another package the account holds. The
  // answer says whether the site's CNAME changed.
  setDomainPackage(id: number, packageId: number): Promise<{
    domain: unknown
    change: { old_cname?: string; new_cname?: string; cname_changed: boolean }
  }> {
    return client.put(`/domains/${id}/package`, { package_id: packageId }).then((r) => r.data)
  },
  // Queues the issuance; the work runs in the control plane's task queue
  // and acmeStatus reports what it did.
  acmeIssue(id: number, body: { email: string; ca?: string }) {
    return client.post(`/domains/${id}/certificates/acme`, body).then((r) => r.data)
  },
  acmeStatus(id: number) {
    return client.get(`/domains/${id}/certificates/acme`).then((r) => r.data)
  },
  // The site's own settings, named as the admin client names them so the
  // site components are one file for both consoles.
  getDomain(id: number) {
    return client.get(`/domains/${id}`).then((r) => r.data)
  },
  updateDomain(id: number, body: Record<string, unknown>) {
    return client.put(`/domains/${id}`, body).then((r) => r.data)
  },
  // 自适应 CC right now: whether nodes are verifying the site's new
  // visitors, since when, and what they saw.
  ccAutoStatus(id: number): Promise<CCAutoStatus> {
    return client.get(`/domains/${id}/cc-auto`).then((r) => r.data)
  },
  updateCCProtection(
    id: number,
    body: {
      level: string
      fail_ban: { enabled: boolean; max_failures: number; window_seconds: number; ban_seconds: number }
      // 自适应 CC: off | loose | normal | strict, and the verification.
      auto?: { level: string; mode: string }
    },
  ) {
    return client.put(`/domains/${id}/cc-protection`, body).then((r) => r.data)
  },
  updateDomainChallenge(id: number, body: Record<string, unknown>) {
    return client.put(`/domains/${id}/challenge`, body).then((r) => r.data)
  },
  updateGeoBlock(
    id: number,
    body: {
      mode: string
      action: string
      regions: string[]
      subdivisions: string[]
      cities: { country: string; name: string }[]
      ban_seconds: number
    },
  ) {
    return client.put(`/domains/${id}/geo-block`, body).then((r) => r.data)
  },
  updateUpstreamLimit(domainId: number, max: number) {
    return client.put(`/domains/${domainId}/upstream-limit`, { upstream_max_concurrent: max }).then((r) => r.data)
  },
  getDomainSyncStatus(domainId: number) {
    return client.get(`/domains/${domainId}/sync-status`).then((r) => r.data)
  },
  // What the site's package includes and how much of its counts is used.
  siteEntitlements(domainId: number): Promise<SiteEntitlements> {
    return client.get(`/domains/${domainId}/entitlements`).then((r) => r.data)
  },
  listHostCerts(domainId: number) {
    return client.get(`/domains/${domainId}/host-certs`).then((r) => r.data)
  },
  bindHostCert(domainId: number, host: string, certId: number) {
    return client.put(`/domains/${domainId}/host-certs/${encodeURIComponent(host)}`, { cert_id: certId }).then((r) => r.data)
  },
  unbindHostCert(domainId: number, host: string) {
    return client.delete(`/domains/${domainId}/host-certs/${encodeURIComponent(host)}`).then((r) => r.data)
  },
  issueHostCert(domainId: number, host: string, body: { email: string; ca?: string }) {
    return client.post(`/domains/${domainId}/host-certs/${encodeURIComponent(host)}/acme`, body).then((r) => r.data)
  },
  retryHostCert(domainId: number, host: string) {
    return client.post(`/domains/${domainId}/host-certs/${encodeURIComponent(host)}/acme/retry`).then((r) => r.data)
  },
  cancelHostCert(domainId: number, host: string) {
    return client.delete(`/domains/${domainId}/host-certs/${encodeURIComponent(host)}/acme`).then((r) => r.data)
  },
  // The caller's certificates, every site's: what the picker offers.
  listAllCertificates() {
    return client.get('/certificates').then((r) => r.data)
  },
  uploadCertificate(domainId: number, body: { cert_pem: string; key_pem: string }) {
    return client.post(`/domains/${domainId}/certificates`, body).then((r) => r.data)
  },
  listOrigins(domainId: number) {
    return client.get(`/domains/${domainId}/origins`).then((r) => r.data)
  },
  addOrigin(domainId: number, body: Record<string, unknown>) {
    return client.post(`/domains/${domainId}/origins`, body).then((r) => r.data)
  },
  deleteOrigin(domainId: number, originId: number) {
    return client.delete(`/domains/${domainId}/origins/${originId}`).then((r) => r.data)
  },
  listCacheJobs(params: { domain_id?: number; kind?: string; limit?: number } = {}) {
    return client.get('/cache/jobs', { params }).then((r) => r.data)
  },
  submitCacheJob(domainId: number, body: { kind: string; targets: string[]; scheduled_at?: string }) {
    return client.post(`/domains/${domainId}/cache/jobs`, body).then((r) => r.data)
  },
  listDomainAliases(domainId: number) {
    return client.get(`/domains/${domainId}/aliases`).then((r) => r.data)
  },
  addDomainAlias(domainId: number, hostname: string) {
    return client.post(`/domains/${domainId}/aliases`, { hostname }).then((r) => r.data)
  },
  deleteDomainAlias(domainId: number, aliasId: number) {
    return client.delete(`/domains/${domainId}/aliases/${aliasId}`).then((r) => r.data)
  },
  // A pending alias's CNAME, checked now: a match makes it active.
  verifyDomainAlias(domainId: number, aliasId: number): Promise<AliasVerifyResult> {
    return client.post(`/domains/${domainId}/aliases/${aliasId}/verify`).then((r) => r.data)
  },

  // Rules — kind ∈ acl | cc | cache | waf | bot.
  listRules(domainId: number, kind: string) {
    return client.get(`/domains/${domainId}/rules/${kind}`).then((r) => r.data)
  },
  createRule(domainId: number, kind: string, body: Record<string, unknown>) {
    return client.post(`/domains/${domainId}/rules/${kind}`, body).then((r) => r.data)
  },
  updateRule(domainId: number, kind: string, ruleId: number, body: Record<string, unknown>) {
    return client.put(`/domains/${domainId}/rules/${kind}/${ruleId}`, body).then((r) => r.data)
  },
  deleteRule(domainId: number, kind: string, ruleId: number) {
    return client.delete(`/domains/${domainId}/rules/${kind}/${ruleId}`).then((r) => r.data)
  },

  // 仪表盘, every route the caller's own account. tz_offset is the
  // browser's minutes east of UTC, so "today" is the customer's today.
  dashboard(params: { tz_offset: number }): Promise<import('@/utils/dashboard').DashSummary> {
    return client.get('/dashboard', { params }).then((r) => r.data)
  },
  // site narrows the figures to one of the caller's sites (the site page).
  dashboardMonitor(params: { range: string; tz_offset: number; site?: number }): Promise<import('@/utils/dashboard').Monitor> {
    return client.get('/dashboard/monitor', { params }).then((r) => r.data)
  },
  dashboardTop(params: { dim: string; range: string; tz_offset: number; site?: number }) {
    return client.get('/dashboard/top', { params }).then((r) => r.data)
  },
  dashboardSites(params: { tz_offset: number }): Promise<import('@/utils/dashboard').DashSites> {
    return client.get('/dashboard/sites', { params }).then((r) => r.data)
  },

  // Stats.
  statsOverview(params: { domain?: string; range?: string; from?: string; to?: string } = {}) {
    return client.get('/stats/overview', { params }).then((r) => r.data)
  },
  statsDomainSeries(domainId: number, range = '24h') {
    return client.get(`/stats/domains/${domainId}/series`, { params: { range } }).then((r) => r.data)
  },
  statsTopURLs(domainId: number, limit = 20) {
    return client.get(`/stats/domains/${domainId}/top-urls`, { params: { limit } }).then((r) => r.data)
  },

  // Open API credentials (per-user AK/SK).
  listCredentials() {
    return client.get('/credentials').then((r) => r.data)
  },
  createCredential(name?: string) {
    return client.post('/credentials', { name: name ?? '' }).then((r) => r.data)
  },
  disableCredential(id: number) {
    return client.delete(`/credentials/${id}`).then((r) => r.data)
  },

  // Certificates list (per domain). Backend endpoint is added in this
  // round — see internal/api/user/domains.go.
  listCertificates(domainId: number) {
    return client.get(`/domains/${domainId}/certificates`).then((r) => r.data)
  },

  // 安全事件: the edge's security log over the caller's sites, and its
  // counts for the same window (hours back from now).
  securityEvents(params: {
    hours: number
    site?: number
    module?: string
    action?: string
    client_ip?: string
    limit?: number
    offset?: number
  }) {
    return client.get('/security/events', { params }).then((r) => r.data)
  },
  securitySummary(params: { hours: number; site?: number }) {
    return client.get('/security/summary', { params }).then((r) => r.data)
  },


  // The account's own IP allow/deny list — applies to every site the
  // account owns.
  listIPEntries(params: { q?: string } = {}) {
    return client.get('/ip-lists', { params }).then((r) => r.data)
  },
  addIPEntry(body: { kind: string; target: string; note?: string }) {
    return client.post('/ip-lists', body).then((r) => r.data)
  },
  // One request rather than a loop — see the admin client for why.
  addIPEntriesBatch(body: { kind: string; targets: string[]; note?: string }) {
    return client.post('/ip-lists/batch', body).then((r) => r.data)
  },
  deleteIPEntry(id: number) {
    return client.delete(`/ip-lists/${id}`).then((r) => r.data)
  },
}

export default client
